import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

export const DEAL_STAGES = ["incarico", "proposta", "compromesso", "rogito", "persa"] as const;
export type DealStage = (typeof DEAL_STAGES)[number];
export const STAGE_LABEL: Record<DealStage, string> = {
  incarico: "Incarico",
  proposta: "Proposta",
  compromesso: "Compromesso",
  rogito: "Rogito",
  persa: "Persa",
};

const Commissions = z.object({ seller: z.number(), buyer: z.number(), total: z.number() });
const Deal = z.object({
  id: z.string().uuid(),
  title: z.string(),
  stage: z.enum(DEAL_STAGES),
  listing_id: z.string().uuid().nullable(),
  unit_id: z.string().uuid().nullable(),
  seller_contact_id: z.string().uuid().nullable(),
  buyer_contact_id: z.string().uuid().nullable(),
  agent_id: z.string().uuid().nullable(),
  price: z.number().nullable(),
  commission_seller_pct: z.number().nullable(),
  commission_seller_amount: z.number().nullable(),
  commission_buyer_pct: z.number().nullable(),
  commission_buyer_amount: z.number().nullable(),
  expected_close: z.string().nullable(),
  closed_at: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  commissions: Commissions,
});
export type Deal = z.infer<typeof Deal>;
const DealsResponse = z.object({ items: z.array(Deal), total: z.number() });

export type DealInput = {
  title: string;
  stage?: DealStage;
  listing_id?: string | null;
  seller_contact_id?: string | null;
  buyer_contact_id?: string | null;
  agent_id?: string | null;
  price?: number | null;
  commission_seller_pct?: number | null;
  commission_seller_amount?: number | null;
  commission_buyer_pct?: number | null;
  commission_buyer_amount?: number | null;
  expected_close?: string | null;
  notes?: string | null;
};

export function useDeals(filters: { stage?: DealStage } = {}) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["deals", filters],
    enabled: !!token,
    queryFn: () => apiFetch("/api/v1/admin/deals", token!, DealsResponse, {
      query: { stage: filters.stage, page_size: 200 },
    }),
  });
}

export function useCreateDeal() {
  const { session } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DealInput) =>
      apiFetch("/api/v1/admin/deals", session!.access_token, Deal, { method: "POST", body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["deals"] }),
  });
}

export function usePatchDeal() {
  const { session } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<DealInput> & { id: string; closed_at?: string | null }) =>
      apiFetch(`/api/v1/admin/deals/${id}`, session!.access_token, Deal, { method: "PATCH", body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["deals"] }),
  });
}

const ReportAgent = z.object({
  agent: z.string(),
  deals: z.number(),
  rogiti: z.number(),
  commissioni_maturate: z.number(),
  commissioni_pipeline: z.number(),
});
const Report = z.object({
  agents: z.array(ReportAgent),
  by_stage: z.record(z.number()),
  totals: z.object({
    deals: z.number(),
    rogiti: z.number(),
    commissioni_maturate: z.number(),
    commissioni_pipeline: z.number(),
  }),
});
export type DealReport = z.infer<typeof Report>;

export function useDealReport() {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["deals-report"],
    enabled: !!token,
    queryFn: () => apiFetch("/api/v1/admin/deals/report", token!, Report),
  });
}

const Proforma = z.object({
  deal: Deal,
  seller: z.object({ id: z.string(), name: z.string(), email: z.string().nullable(), phone: z.string().nullable() }).nullable(),
  buyer: z.object({ id: z.string(), name: z.string(), email: z.string().nullable(), phone: z.string().nullable() }).nullable(),
  listing: z.object({ id: z.string(), slug: z.string(), title: z.string().nullable(), city: z.string().nullable() }).nullable(),
  generated_at: z.string(),
  disclaimer: z.string(),
});
export type ProformaData = z.infer<typeof Proforma>;

export function useProforma(dealId: string | undefined) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["proforma", dealId],
    enabled: !!token && !!dealId,
    queryFn: () => apiFetch(`/api/v1/admin/deals/${dealId}/proforma`, token!, Proforma),
  });
}
